import { zodResolver } from "@hookform/resolvers/zod";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Input, Modal, PhoneInput, Select } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { RiAddLargeFill } from "react-icons/ri";

import CRMUsersModel from "../../../Models/CRM/CRMUsers.Model";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { AddUserFormData, addUserSchema } from "./addUserSchema";

const CRMAddUserModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(undefined);
  const { locations } = LocationsModel.useState((s) => s);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
  }, []);

  const locationOptions = (locations ?? []).map((loc) => ({
    label: loc.name,
    value: String(loc.id)
  }));
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    trigger,
    watch,
    reset,
    formState: { errors, isSubmitting, isValid, touchedFields }
  } = useForm<AddUserFormData>({
    resolver: zodResolver(addUserSchema),
    mode: "onBlur",
    resetOptions: {
      keepErrors: false,
      keepDirty: false,
      keepValues: false,
      keepIsValidating: false
    }
  });

  const values = watch();
  const phoneRegistration = register("phoneNumber");
  const isFormValid = isValid && !!values.name && !!values.email && !!values.phoneNumber;

  const handleAddNewUser = async (data: AddUserFormData) => {
    try {
      await CRMUsersModel.createCRMUser({
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
        locationId: data.locationId ? Number(data.locationId) : undefined
      });

      handleClose();
    } catch (error) {
      setError("root", { message: "An unexpected error occurred. Please try again." });
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedLocationId(undefined);
    reset();
  };

  const toggleOpen = () => {
    if (isOpen) {
      handleClose();
    } else {
      setIsOpen(true);
    }
  };

  return (
    <Modal
      title="Add a Contact"
      onClose={toggleOpen}
      isOpen={isOpen}
      trigger={
        <Button>
          <RiAddLargeFill /> Add New
        </Button>
      }
    >
      <form className="flex h-full flex-col gap-4 px-4 pb-4" onSubmit={handleSubmit(handleAddNewUser)}>
        <div className="flex flex-col gap-2">
          <Input
            {...register("name")}
            autoFocus
            id="name"
            label="Name"
            required
            type="text"
            error={touchedFields.name ? errors.name?.message : undefined}
          />
          <PhoneInput
            id="phoneNumber"
            label="Phone Number"
            required
            type="text"
            error={touchedFields.phoneNumber ? errors.phoneNumber?.message : undefined}
            name={phoneRegistration.name}
            ref={phoneRegistration.ref}
            onBlur={() => {
              void trigger("phoneNumber");
            }}
            onChange={(fullNumber) => setValue("phoneNumber", fullNumber, { shouldDirty: true, shouldTouch: true, shouldValidate: true })}
          />
          <Input
            {...register("email")}
            id="email"
            label="Email"
            required
            type="email"
            error={touchedFields.email ? errors.email?.message : undefined}
          />
          <Select
            options={locationOptions}
            selectedValues={selectedLocationId ? [selectedLocationId] : []}
            onChange={(value) => {
              setSelectedLocationId(value[0]);
              setValue("locationId", value[0], { shouldValidate: true });
            }}
            label="Location"
          />
        </div>

        {errors.root?.message && <p className="text-sm text-red-500">{errors.root.message}</p>}
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose} type="button" size={ButtonSize.SMALL} variant={ButtonVariant.GHOST}>
            Cancel
          </Button>
          <Button disabled={!isFormValid} loading={isSubmitting} type="submit" color={ButtonColor.ORANGE} size={ButtonSize.SMALL}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export { CRMAddUserModal };
